import { useYoutubePlayer } from "@app/resources/hooks";
import { Resource } from "@core/shared/types";
import { cn } from "@design-system/utils";
import { trans, plainTrans } from "@mongez/localization";
import {
  AlertTriangle,
  Gauge,
  LoaderCircle,
  Maximize,
  Minimize,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Smartphone,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import YouTube from "react-youtube";

type ResourceYoutubeVideoProps = {
  resource: Resource;
};

type PlayerControlButtonProps = {
  label: string;
  children: ReactNode;
  className?: string;
  onClick: () => void;
};

const PLAYBACK_SPEEDS = [0.5, 1, 1.25, 1.5, 2];

function formatPlayerTime(seconds: number) {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = Math.floor(safeSeconds % 60);
  const minuteValue = hours > 0 ? String(minutes).padStart(2, "0") : minutes;

  return [
    ...(hours > 0 ? [hours] : []),
    minuteValue,
    String(remainingSeconds).padStart(2, "0"),
  ].join(":");
}

function PlayerControlButton({
  label,
  children,
  className,
  onClick,
}: PlayerControlButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/10 text-white backdrop-blur-md transition hover:scale-105 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 sm:h-10 sm:w-10",
        className,
      )}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

/**
 * Displays a YouTube-backed resource inside the platform's own player chrome.
 */
export function ResourceYoutubeVideo({ resource }: ResourceYoutubeVideoProps) {
  const {
    containerRef,
    currentTime,
    duration,
    handleFullscreenToggle,
    handleMuteToggle,
    handleOverlayClick,
    handleOverlayKeyDown,
    handlePlaybackRateChange,
    handlePlayPause,
    handleProgressClick,
    handleProgressKeyDown,
    handleVolumeChange,
    hasError,
    hideControls,
    isFullscreen,
    isMuted,
    isPlaying,
    isPseudoFullscreen,
    isReady,
    onError,
    onReady,
    onStateChange,
    opts,
    playbackRate,
    progress,
    revealControls,
    showControls,
    skipBackward,
    skipForward,
    videoId,
    volume,
  } = useYoutubePlayer(resource);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);

  useEffect(() => {
    if (!showControls) {
      setShowSpeedMenu(false);
    }
  }, [showControls]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "group relative aspect-video w-full overflow-hidden rounded-2xl border border-slate-800/70 bg-slate-950 shadow-2xl shadow-slate-950/20 sm:rounded-3xl",
        // CSS pseudo-fullscreen (iPhone Safari has no element fullscreen API).
        isPseudoFullscreen &&
          "fixed inset-0 z-[2147483000] aspect-auto h-[100dvh] w-screen rounded-none border-0 shadow-none sm:rounded-none",
      )}
      data-pseudo-fullscreen={isPseudoFullscreen || undefined}
      onMouseEnter={revealControls}
      onMouseLeave={hideControls}
      onMouseMove={revealControls}
      onTouchStart={revealControls}
    >
      <div className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(circle_at_top_right,rgba(109,40,217,0.16),transparent_35%)]" />

      {!hasError && (
        <YouTube
          videoId={videoId ?? undefined}
          opts={opts}
          onReady={onReady}
          onStateChange={onStateChange}
          onError={onError}
          title={resource.title || trans("youtubePlayer.videoLesson")}
          loading="lazy"
          className="pointer-events-none h-full w-full"
          iframeClassName="pointer-events-none h-full w-full scale-[1.015]"
        />
      )}

      {!hasError && (
        <div
          className="absolute inset-0 z-10 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-300"
          onClick={handleOverlayClick}
          onKeyDown={handleOverlayKeyDown}
          tabIndex={0}
          role="button"
          aria-label={
            isPlaying
              ? trans("youtubePlayer.pause")
              : trans("youtubePlayer.play")
          }
        />
      )}

      {!isReady && !hasError && (
        <div className="absolute inset-0 z-20 grid place-items-center bg-slate-950">
          <div className="flex flex-col items-center gap-3 text-slate-200">
            <LoaderCircle className="h-9 w-9 animate-spin text-violet-400" />
            <span className="text-sm font-bold">
              {trans("youtubePlayer.loading")}
            </span>
          </div>
        </div>
      )}

      {hasError && (
        <div className="absolute inset-0 z-30 grid place-items-center bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 p-6 text-center">
          <div className="max-w-md">
            <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-amber-400/10 text-amber-300 ring-1 ring-amber-300/20">
              <AlertTriangle className="h-7 w-7" />
            </span>
            <h3 className="text-lg font-black text-white sm:text-xl">
              {trans("youtubePlayer.unavailableTitle")}
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              {trans("youtubePlayer.unavailableDescription")}
            </p>
          </div>
        </div>
      )}

      {isPseudoFullscreen && showControls && (
        <div className="pointer-events-none absolute inset-x-0 top-20 z-30 flex justify-center px-4 landscape:hidden">
          <span className="flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/80 px-3 py-1.5 text-xs font-bold text-white/90 backdrop-blur-md">
            <Smartphone className="h-4 w-4 rotate-90" />
            {trans("youtubePlayer.rotateForFullscreen")}
          </span>
        </div>
      )}

      {!hasError && isReady && !isPlaying && (
        <button
          type="button"
          onClick={() => void handlePlayPause()}
          className="absolute left-1/2 top-1/2 z-30 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/25 bg-white/15 text-white shadow-2xl backdrop-blur-xl transition hover:scale-110 hover:bg-violet-500 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-300/50 sm:h-20 sm:w-20"
          aria-label={plainTrans("youtubePlayer.play")}
        >
          <Play className="ms-1 h-7 w-7 fill-current sm:h-9 sm:w-9" />
        </button>
      )}

      {!hasError && (
        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 top-0 z-20 bg-gradient-to-b from-slate-950/90 via-slate-950/45 to-transparent px-4 pb-12 pt-4 transition-opacity duration-300 sm:px-6 sm:pt-5",
            showControls ? "opacity-100" : "opacity-0",
          )}
        >
          <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="min-w-0">
              <span className="mb-1 inline-flex rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-[10px] font-black text-violet-100 backdrop-blur-md sm:text-xs">
                {trans("youtubePlayer.videoLesson")}
              </span>
              <h3 className="truncate text-sm font-black text-white drop-shadow sm:text-lg">
                {resource.title}
              </h3>
            </div>
          </div>
        </div>
      )}

      {!hasError && (
        <div
          className={cn(
            "absolute inset-x-0 bottom-0 z-40 bg-gradient-to-t from-slate-950 via-slate-950/90 to-transparent px-3 pb-3 pt-16 transition-opacity duration-300 sm:px-5 sm:pb-5 sm:pt-24",
            showControls
              ? "pointer-events-auto opacity-100"
              : "pointer-events-none opacity-0",
          )}
        >
          <div
            dir="ltr"
            className="group/progress relative mb-2 h-5 w-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
            onClick={handleProgressClick}
            onKeyDown={handleProgressKeyDown}
            tabIndex={0}
            role="slider"
            aria-label={plainTrans("youtubePlayer.progress")}
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuetext={plainTrans("youtubePlayer.progressValue", {
              progress: Math.round(progress),
            })}
          >
            <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full bg-white/25">
              <div
                className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-violet-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div
              className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-violet-500 opacity-0 shadow-lg transition group-hover/progress:opacity-100 group-focus/progress:opacity-100"
              style={{ left: `${progress}%` }}
            />
          </div>

          <div className="flex items-center justify-between gap-2" dir="ltr">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <PlayerControlButton
                label={
                  isPlaying
                    ? trans("youtubePlayer.pause")
                    : trans("youtubePlayer.play")
                }
                onClick={() => void handlePlayPause()}
                className="bg-violet-500 hover:bg-violet-400"
              >
                {isPlaying ? (
                  <Pause className="h-4 w-4 fill-current sm:h-5 sm:w-5" />
                ) : (
                  <Play className="ms-0.5 h-4 w-4 fill-current sm:h-5 sm:w-5" />
                )}
              </PlayerControlButton>

              <PlayerControlButton
                label={trans("youtubePlayer.skipBackward")}
                onClick={() => void skipBackward()}
                className="hidden min-[420px]:grid"
              >
                <RotateCcw className="h-4 w-4" />
              </PlayerControlButton>

              <PlayerControlButton
                label={trans("youtubePlayer.skipForward")}
                onClick={() => void skipForward()}
                className="hidden min-[420px]:grid"
              >
                <RotateCw className="h-4 w-4" />
              </PlayerControlButton>

              <div className="ms-0.5 flex items-center gap-2 sm:ms-1">
                <PlayerControlButton
                  label={
                    isMuted
                      ? trans("youtubePlayer.unmute")
                      : trans("youtubePlayer.mute")
                  }
                  onClick={() => void handleMuteToggle()}
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="h-4 w-4" />
                  ) : (
                    <Volume2 className="h-4 w-4" />
                  )}
                </PlayerControlButton>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={(event) => void handleVolumeChange(event)}
                  className="hidden h-1 w-20 cursor-pointer accent-violet-500 md:block lg:w-24"
                  aria-label={plainTrans("youtubePlayer.volume")}
                />
              </div>

              <span className="ms-1 whitespace-nowrap text-[11px] font-bold tabular-nums text-white/80 sm:text-xs">
                {formatPlayerTime(currentTime)} / {formatPlayerTime(duration)}
              </span>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowSpeedMenu((isVisible) => !isVisible)}
                  className="flex h-9 min-w-12 items-center justify-center gap-1 rounded-full border border-white/10 bg-white/10 px-2 text-[11px] font-black text-white backdrop-blur-md transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 sm:h-10 sm:min-w-0 sm:gap-1.5 sm:px-3 sm:text-xs"
                  aria-label={plainTrans("youtubePlayer.playbackSpeed")}
                  aria-expanded={showSpeedMenu}
                >
                  <Gauge className="hidden h-4 w-4 min-[390px]:block" />
                  <span>{playbackRate}x</span>
                </button>

                {showSpeedMenu && (
                  <div className="absolute bottom-full right-0 mb-2 min-w-28 overflow-hidden rounded-2xl border border-white/10 bg-slate-950/95 p-1.5 shadow-2xl backdrop-blur-xl">
                    {PLAYBACK_SPEEDS.map((speed) => (
                      <button
                        type="button"
                        key={speed}
                        onClick={() => {
                          void handlePlaybackRateChange(speed);
                          setShowSpeedMenu(false);
                        }}
                        className={cn(
                          "flex w-full items-center justify-between rounded-xl px-3 py-2 text-xs font-bold transition hover:bg-white/10",
                          playbackRate === speed
                            ? "bg-violet-500/20 text-violet-200"
                            : "text-white",
                        )}
                      >
                        {speed === 1
                          ? trans("youtubePlayer.normalSpeed")
                          : `${speed}x`}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <PlayerControlButton
                label={
                  isFullscreen
                    ? trans("youtubePlayer.exitFullscreen")
                    : trans("youtubePlayer.enterFullscreen")
                }
                onClick={() => void handleFullscreenToggle()}
              >
                {isFullscreen ? (
                  <Minimize className="h-4 w-4" />
                ) : (
                  <Maximize className="h-4 w-4" />
                )}
              </PlayerControlButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
